import type { ReactNode } from 'react';
import { AppSidebar } from './AppSidebar';
import { BottomNav } from './BottomNav';
import { DataSourceBanner } from './DataSourceBanner';
import { AccessReadFailedBanner } from './AccessReadFailedBanner';
import { PauseOfferBanner } from './PauseOfferBanner';
import { ProTrialBanner } from './ProTrialBanner';
import { ViewAsBanner } from './ViewAsBanner';
import { DemoBanner } from '@/features/demo/DemoBanner';
import { useFirstLoginRedirect } from '@/features/demo/useFirstLoginRedirect';
import { UnsavedWorkProvider } from '@/components/settings/UnsavedChangesGuard';

export interface AppShellProps {
  children: ReactNode;
  /** Props forwarded to AppSidebar */
  notificationCount?: number;
  alertsBadgeCount?: number;
  onCommandPaletteOpen?: () => void;
}

export function AppShell({
  children,
  notificationCount,
  alertsBadgeCount,
  onCommandPaletteOpen,
}: AppShellProps) {
  useFirstLoginRedirect();
  return (
    // Above the sidebar on purpose: switching organization is not a router
    // navigation, so the switcher itself has to know whether a screen below it
    // is holding unsaved edits.
    <UnsavedWorkProvider>
      <div data-testid="app-shell" className="flex h-screen bg-bg-void">
        {/* Desktop Sidebar (hidden on mobile) */}
        <div className="hidden md:block relative">
          <AppSidebar
            notificationCount={notificationCount}
            alertsBadgeCount={alertsBadgeCount}
            onCommandPaletteOpen={onCommandPaletteOpen}
          />
        </div>

        {/* Main Content Area */}
        <div className="flex flex-1 flex-col overflow-hidden min-w-0">
          <ViewAsBanner />
          <DemoBanner />
          <AccessReadFailedBanner />
          <DataSourceBanner />
          <PauseOfferBanner />
          <ProTrialBanner />
          <main
            id="main-content"
            className="flex-1 overflow-auto bg-bg-void pb-16 md:pb-0"
          >
            {children}
          </main>
        </div>

        {/* Mobile Bottom Nav */}
        <BottomNav />
      </div>
    </UnsavedWorkProvider>
  );
}
